import React, { useCallback, useMemo, useState } from 'react';
import { withRouter, type RouteComponentProps } from 'react-router-dom';

import LegalPageLayout from 'components/LegalPageLayout';
import LocalizedLink from 'components/LocalizedLink';
import { useI18n } from 'i18n/I18nContext';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

type TierId = 'starter' | 'pro' | 'enterprise';

type ModuleId =
  | 'module_cashregister'
  | 'module_timesheet'
  | 'module_reservations'
  | 'module_mep'
  | 'module_facility';

const TIERS: { id: TierId; eur: number; staffKey: string }[] = [
  { id: 'starter', eur: 19, staffKey: 'staffStarter' },
  { id: 'pro', eur: 49, staffKey: 'staffPro' },
  { id: 'enterprise', eur: 99, staffKey: 'staffEnterprise' },
];

const MODULES: { id: ModuleId; eur: number; labelKey: string }[] = [
  { id: 'module_cashregister', eur: 15, labelKey: 'modCash' },
  { id: 'module_timesheet', eur: 15, labelKey: 'modTimesheet' },
  { id: 'module_reservations', eur: 15, labelKey: 'modReservations' },
  { id: 'module_mep', eur: 10, labelKey: 'modMep' },
  { id: 'module_facility', eur: 10, labelKey: 'modFacility' },
];

function formatEur(amount: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : 'en-NL', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `€${amount}`;
  }
}

const PricingPage = ({ location }: RouteComponentProps) => {
  const { t, locale } = useI18n();
  const [tier, setTier] = useState<TierId>('pro');
  const [modules, setModules] = useState<ModuleId[]>([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const cancelled = /[?&]checkout=cancel(?:&|$)/.test(location.search || '');

  const selectedTier = TIERS.find((row) => row.id === tier) || TIERS[1];
  const moduleTotal = modules.reduce((sum, id) => {
    const row = MODULES.find((m) => m.id === id);
    return sum + (row ? row.eur : 0);
  }, 0);
  const monthlyTotal = selectedTier.eur + moduleTotal;

  const lineItems = useMemo(() => {
    const items = [
      {
        key: `tier-${tier}`,
        label: t(`pricing.tiers.${tier}.name`),
        amount: selectedTier.eur,
      },
    ];
    modules.forEach((id) => {
      const row = MODULES.find((m) => m.id === id);
      if (!row) return;
      items.push({
        key: id,
        label: t(`pricing.modules.${row.labelKey}`),
        amount: row.eur,
      });
    });
    return items;
  }, [tier, modules, selectedTier.eur, t]);

  const toggleModule = useCallback((id: ModuleId) => {
    setModules((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
    setCheckoutError(null);
  }, []);

  const startCheckout = useCallback(async () => {
    if (!API_URL) {
      setCheckoutError(t('pricing.errors.apiMissing'));
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const res = await fetch(`${API_URL}/api/billing/marketing-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ tier, modules, locale }),
      });
      const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        setCheckoutError(data.error || t('pricing.errors.checkoutFailed'));
        setCheckoutLoading(false);
        return;
      }
      window.location.assign(data.url);
    } catch {
      setCheckoutError(t('pricing.errors.network'));
      setCheckoutLoading(false);
    }
  }, [tier, modules, locale, t]);

  return (
    <LegalPageLayout
      title={t('pricing.title')}
      lede={t('pricing.intro')}
      variant="marketing"
      wide
      extraWide
    >
      {cancelled ? (
        <p className="gb-pricing-banner" role="status">
          {t('pricing.cancelled')}
        </p>
      ) : null}

      <div className="gb-pricing">
        <div className="gb-pricing__config">
          <section className="gb-pricing__section" aria-labelledby="pricing-tiers-heading">
            <h2 id="pricing-tiers-heading" className="gb-pricing__heading">
              {t('pricing.tiersHeading')}
            </h2>
            <p className="gb-pricing__hint">{t('pricing.tiersHint')}</p>
            <div
              className="gb-pricing__tiers"
              role="radiogroup"
              aria-labelledby="pricing-tiers-heading"
            >
              {TIERS.map((row) => {
                const selected = tier === row.id;
                return (
                  <button
                    key={row.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`gb-pricing__tier${selected ? ' is-selected' : ''}`}
                    onClick={() => {
                      setTier(row.id);
                      setCheckoutError(null);
                    }}
                  >
                    <span className="gb-pricing__tier-name">
                      {t(`pricing.tiers.${row.id}.name`)}
                    </span>
                    <span className="gb-pricing__tier-price">
                      {formatEur(row.eur, locale)}
                      <span className="gb-pricing__per">{t('pricing.perMonth')}</span>
                    </span>
                    <span className="gb-pricing__tier-staff">{t(`pricing.${row.staffKey}`)}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="gb-pricing__section" aria-labelledby="pricing-modules-heading">
            <h2 id="pricing-modules-heading" className="gb-pricing__heading">
              {t('pricing.modulesHeading')}
            </h2>
            <p className="gb-pricing__hint">{t('pricing.modulesHint')}</p>
            <p className="gb-pricing__included">{t('pricing.included')}</p>
            <ul className="gb-pricing__modules">
              {MODULES.map((row) => {
                const checked = modules.includes(row.id);
                return (
                  <li key={row.id}>
                    <label className={`gb-pricing__module${checked ? ' is-selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleModule(row.id)}
                      />
                      <span className="gb-pricing__module-label">
                        {t(`pricing.modules.${row.labelKey}`)}
                      </span>
                      <span className="gb-pricing__module-price">
                        {formatEur(row.eur, locale)}
                        <span className="gb-pricing__per">{t('pricing.perLocationMonth')}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>

        <aside className="gb-pricing__summary" aria-labelledby="pricing-summary-heading">
          <h2 id="pricing-summary-heading" className="gb-pricing__heading">
            {t('pricing.summaryHeading')}
          </h2>
          <ul className="gb-pricing__lines">
            {lineItems.map((item) => (
              <li key={item.key}>
                <span>{item.label}</span>
                <span>{formatEur(item.amount, locale)}</span>
              </li>
            ))}
          </ul>
          <p className="gb-pricing__total">
            <span>{t('pricing.total')}</span>
            <strong>
              {formatEur(monthlyTotal, locale)}
              <span className="gb-pricing__per">{t('pricing.perMonth')}</span>
            </strong>
          </p>
          <p className="gb-pricing__summary-note">{t('pricing.summaryNote')}</p>

          {checkoutError ? (
            <p className="gb-pricing__error" role="alert">
              {checkoutError}
            </p>
          ) : null}

          <button
            type="button"
            className="gb-btn bg-green shadow text-white text-lg font-bold px-25 py-20 rounded-lg gb-pricing__checkout"
            onClick={startCheckout}
            disabled={checkoutLoading}
          >
            {checkoutLoading ? t('pricing.checkoutLoading') : t('pricing.checkoutCta')}
          </button>

          <p className="gb-pricing__trial">
            <LocalizedLink className="text-green font-bold no-underline" to="/onboard">
              {t('pricing.trialCta')}
            </LocalizedLink>
          </p>

          {tier === 'enterprise' ? (
            <p className="gb-pricing__contact">
              <LocalizedLink className="text-green font-bold no-underline" to="/contact">
                {t('pricing.enterpriseContact')}
              </LocalizedLink>
            </p>
          ) : null}
        </aside>
      </div>

      <p className="gb-pricing-footnote">{t('pricing.footnote')}</p>
    </LegalPageLayout>
  );
};

export default withRouter(PricingPage);



// WEBPACK FOOTER //
// ./src/pages/PricingPage.tsx